import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, FileText, Clock, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import PackageCard from "@/components/PackageCard";
import { api, track } from "@/lib/api";

const GEO = "https://images.unsplash.com/photo-1746468659044-473ffb96b934?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxzYWNyZWQlMjBnZW9tZXRyeSUyMGNlbGVzdGlhbCUyMHBvcnRyYWl0JTIwbXlzdGljYWwlMjBlZGl0b3JpYWx8ZW58MHx8fHwxNzkwNzA5MzEyfDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: FileText, title: "Choose & share details", text: "Pick a report and enter your name and birth details in a private, multi-step form." },
  { icon: ShieldCheck, title: "Pay securely", text: "Pay safely in INR with UPI or card — your report is only prepared once your payment is confirmed." },
  { icon: Sparkles, title: "We calculate & write", text: "Your numbers are calculated using traditional numerology methods, then woven into a reading written just for you." },
  { icon: Mail, title: "Delivered by email", text: "Your polished PDF arrives within approximately 6–7 hours — no dashboards to check." },
];

export default function Home() {
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    track("landing_view");
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <Seo title="Personalized Numerology Reports" path="/" />
      {/* Hero */}
      <section className="hero-grain relative overflow-hidden bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="eyebrow mb-5">
              Pythagorean &amp; Chaldean · Delivered as PDF
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-navy"
            >
              Your numbers, read with <span className="italic text-gold-hover">care and depth.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              A numerology reading crafted around your name and birth date — your numbers calculated
              with care, then written into a personal, beautifully prepared report you'll want to
              return to.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link to="/pricing" data-testid="hero-cta-primary" className="btn-gold">
                Get Your Report <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/sample-report" data-testid="hero-cta-sample" className="btn-ghost">
                View a Sample
              </Link>
            </motion.div>
            <p className="mt-6 text-xs text-slate-400 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Reports typically delivered within ~6–7 hours of confirmed payment.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15, duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border border-gold/20">
              <img src={GEO} alt="Sacred geometry motif" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl border border-border px-6 py-4">
              <p className="eyebrow mb-1">Life Path</p>
              <p className="font-serif text-4xl text-navy">7 <span className="text-gold text-2xl">· seeker</span></p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value strip */}
      <section className="border-y border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            ["Personal to you", "Calculated from your details"],
            ["Thoughtfully written", "A reading, not a template"],
            ["Secure & simple", "Pay safely in minutes"],
            ["Private by design", "Your details stay yours"],
          ].map(([t, s]) => (
            <div key={t}>
              <p className="font-serif text-xl text-navy">{t}</p>
              <p className="text-xs text-slate-500 mt-1">{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="eyebrow mb-3">How it works</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight">
            From your birth details to a report you'll keep.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl border border-border p-7 card-lift">
              <div className="w-11 h-11 rounded-full bg-gold-subtle flex items-center justify-center mb-5">
                <s.icon className="w-5 h-5 text-gold-hover" />
              </div>
              <p className="font-mono-accent text-xs text-gold-hover mb-2">0{i + 1}</p>
              <h3 className="font-serif text-2xl text-navy mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section className="bg-paper py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow mb-3">Choose your report</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy">Three thoughtful depths</h2>
            <p className="mt-4 text-slate-600">Every report is personalized. Pick the depth that suits you.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
            {packages.map((p) => <PackageCard key={p.slug} pkg={p} />)}
          </div>
        </div>
      </section>

      {/* Honest disclaimer band */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-16 text-center">
        <p className="text-sm text-slate-500 leading-relaxed">
          Discover a more personal perspective on yourself through the timeless traditions of
          numerology. Each Aetheria report brings together your details, meaningful number
          interpretations, and thoughtful insight — a beautifully prepared reading, made just for you.
        </p>
      </section>
    </>
  );
}
